import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AxiosError } from "axios";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import MetaLogo from "@/assets/meta-logo.svg?react";
import RunMetaCampaignTab from "@/pages/brand/meta-ads/RunMetaCampaignTab";
import { rowsOf } from "@/pages/brand/meta-ads/insights";
import { fetchFacebookPages, getMetaAdsStatus, type MetaAdsStatus } from "@/services/metaAds.service";

function errMessage(e: unknown): string {
  if (e instanceof AxiosError) {
    const d = e.response?.data as { message?: string | string[] } | undefined;
    if (d?.message) return Array.isArray(d.message) ? d.message.join(", ") : d.message;
    return e.message || "Request failed";
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

/**
 * The program's Launch tab is the Meta Ads launch flow with the program
 * fixed, so the two places a brand can launch from look and behave the same.
 */
export function ContentProgramLaunchTab({
  programId,
  initialContentId,
  onLaunched,
}: {
  programId: string;
  /** Creative to preselect, e.g. the one just approved from a TikTok ad request. */
  initialContentId?: string | null;
  onLaunched?: () => void;
}) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<MetaAdsStatus | null>(null);
  const [pages, setPages] = useState<{ id: string; name?: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const s = await getMetaAdsStatus();
        if (cancelled) return;
        setStatus(s);
        if (s.connected) {
          const res = await fetchFacebookPages();
          if (cancelled) return;
          setPages(
            rowsOf(res)
              .map((r) => ({ id: String(r.id ?? ""), name: typeof r.name === "string" ? r.name : undefined }))
              .filter((r) => r.id)
          );
        }
      } catch (e) {
        if (!cancelled) toast.error(errMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const accountId = status?.defaultAdAccountId?.trim() || "";

  if (!loading && (!status?.connected || !accountId)) {
    return (
      <EmptyState
        icon={<MetaLogo className="size-5" />}
        title={status?.connected ? "Choose a default ad account" : "Connect Meta to launch ads"}
        body={
          status?.connected
            ? "Pick the ad account campaigns are created in under Meta Ads"
            : "Approved creative launches into your connected Meta ad account"
        }
        action={
          <Button variant="outline" onClick={() => navigate("/brand/meta-ads")}>
            Open Meta Ads
          </Button>
        }
      />
    );
  }

  return (
    <RunMetaCampaignTab
      fixedProgramId={programId}
      initialContentId={initialContentId}
      pages={pages}
      pagesLoading={loading}
      defaultFacebookPageId={status?.defaultFacebookPageId ?? ""}
      defaultAdAccountId={accountId}
      metaPixelId={status?.metaPixelId ?? null}
      onLaunched={onLaunched}
    />
  );
}
